import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { environment } from '@environment';
import { patchState, signalState } from '@ngrx/signals';
import { firstValueFrom, map } from 'rxjs';
import { Badge } from '~/domain/types/badge';
import { Company, CreateCompany, UpdateCompany } from '~/domain/types/company';
import { Device } from '~/domain/types/device';
import { Pageable, Paginated, Pagination, Searchable, Sort, Sortable } from '~/domain/types/listings';
import { pluck } from '~/utils/common';

type State = {
  q: string;
  sort: Sort;
  pagination: Pagination;
  companies: Company[] | null;
};

@Service()
export class CompaniesService {
  private readonly _http = inject(HttpClient);
  private readonly _state = signalState<State>({
    q: '',
    sort: { property: 'name', direction: 'asc' },
    pagination: { number: 1, size: 10, totalElements: 0, totalPages: 0 },
    companies: null,
  });

  readonly searchQuery = this._state.q;
  readonly sort = this._state.sort;
  readonly pagination = this._state.pagination;
  readonly companies = this._state.companies;

  async loadCompanies(options: Partial<Searchable & Pageable & Sortable> = {}): Promise<void> {
    const { q = this.searchQuery(), page = this.pagination(), sort = this.sort() } = options;
    const params = {
      q,
      page: page.number.toString(),
      size: page.size.toString(),
      orderBy: sort.property,
      sort: sort.direction,
    };

    patchState(this._state, { q, sort });

    const { content, ...pagination } = await firstValueFrom(
      this._http.get<Paginated<Company>>(`${environment.api}/companies`, { params }),
    );
    patchState(this._state, { companies: content, pagination });
  }

  fetchCompany(id: string): Promise<Company> {
    return firstValueFrom(this._http.get<Company>(`${environment.api}/companies/${id}`));
  }

  createCompany(data: CreateCompany): Promise<Company> {
    return firstValueFrom(this._http.post<Company>(`${environment.api}/companies`, data));
  }

  updateCompany(id: string, data: UpdateCompany): Promise<Company> {
    return firstValueFrom(this._http.patch<Company>(`${environment.api}/companies/${id}`, data));
  }

  archiveCompany(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/companies/${id}/archive`, null));
  }

  restoreCompany(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/companies/${id}/restore`, null));
  }

  fetchDevices(): Promise<Device[]> {
    const params = { size: 10, orderBy: 'serialNumber', sort: 'asc', scope: 'unassigned' };

    return firstValueFrom(
      this._http.get<Paginated<Device>>(`${environment.api}/devices`, { params }).pipe(map(pluck('content'))),
    );
  }

  fetchBadges(): Promise<Badge[]> {
    const params = { size: 50, orderBy: 'number', sort: 'asc', scope: 'unassigned' };

    return firstValueFrom(
      this._http.get<Paginated<Badge>>(`${environment.api}/badges`, { params }).pipe(map(pluck('content'))),
    );
  }

  assignDevices(companyId: string, deviceIds: string[]): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/companies/${companyId}/devices`, { deviceIds }));
  }

  assignBadges(companyId: string, badgeIds: string[]): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/companies/${companyId}/badges`, { badgeIds }));
  }
}
